import { useUserConfiguration } from "@hooks/useUserConfiguration";
import { Avatar, Chip, IconButton, Row, Tag } from "@narmi/design_system";
import { getInitials } from "@utils/name";
import Filters from "byzantine/src/filters";
import { phoneFormatter } from "cerulean/src/PhoneTextInput";
import { $staffApi } from "../../../api";
import { Thread } from "../types";
import styles from "./style.module.css";
import { PanelRow } from "./ThreadDetailsPanel";

export function ThreadUserDetails({ thread }: { thread: Thread }) {
  const { features } = useUserConfiguration();

  const { data: user } = $staffApi.useSuspenseQuery("get", "/api/profile/{uuid}/", {
    params: {
      path: {
        uuid: thread.user.id,
      },
    },
  });

  return (
    <div className={styles.detailsPanelSection}>
      <PanelRow
        iconName="user"
        label="user info"
        href={
          features.includes("customer_profile") ? `/users/${user.uuid}` : `/users/${user.uuid}/old/`
        }
      />
      <div className={styles.threadUserDetailsContainer}>
        <Row alignItems="center" gapSize="s">
          <Row.Item shrink>
            <Avatar
              kind="tonal"
              initials={getInitials(user.full_name)}
              size="m"
              label={user.full_name}
            />
          </Row.Item>
          <Row.Item>
            <p className="fontWeight--semibold">{user.full_name}</p>
          </Row.Item>
        </Row>
        <Row gapSize="xs">
          <Row.Item shrink>
            <Chip kind="secondary" label={Filters.humanize(user.user_category)} />
          </Row.Item>
          {user.organization ? (
            <Row.Item>
              <Chip
                kind="secondary"
                label={
                  user.organization_role.length > 25
                    ? user.organization_role.slice(0, 25).trim() + "..."
                    : user.organization_role
                }
              />
            </Row.Item>
          ) : null}
        </Row>
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-xs)" }}>
          <DetailRow label="Username" value={user.username} />
          <DetailRow label="ID" value={user.membership_id} />
          <DetailRow label="Phone" value={phoneFormatter(user.phone)} />
          <DetailRow label="Email" value={user.email} />
          <DetailRow label="Email on core" value={user.core_provided_email} />
          <DetailRow label="Address" value={user.human_facing_address} />
          <DetailRow
            label="Began enrollment"
            value={Filters.shortMonthDayYearTime(user.date_joined)}
          />
          <DetailRow
            label="Completed enrollment"
            value={
              user.finished_enrollment_at &&
              Filters.shortMonthDayYearTime(user.finished_enrollment_at)
            }
          />
          <DetailRow
            label="Last login"
            value={user.last_login ? Filters.shortMonthDayYearTime(user.last_login) : "n/a"}
          />
        </div>
        {user.organization && (
          <>
            <div className={styles.separatorLine} />
            <Row alignItems="center" gapSize="s">
              <Row.Item shrink>
                <Avatar
                  initials={getInitials(user.organization.name)}
                  size="m"
                  label={user.organization.name}
                />
              </Row.Item>
              <Row.Item>
                <p className="fontWeight--semibold">{user.organization.name}</p>
              </Row.Item>
              <Row.Item shrink>
                <IconButton
                  name="external-link"
                  kind="action"
                  textSize="m"
                  as="a"
                  target="_blank"
                  href={`/organizations/${user.organization.uuid}`}
                />
              </Row.Item>
            </Row>
            {user.organization.type && (
              <div>
                <Tag label={Filters.humanize(user.organization.type)} />
              </div>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-xs)" }}>
              <DetailRow label="Business phone" value={phoneFormatter(user.organization.phone)} />
              <DetailRow label="Business address" value={user.organization.address} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value?: string | null }) {
  if (!value) {
    return null;
  }

  return (
    <Row alignItems="center" gapSize="xs" justifyContent="end">
      <Row.Item>
        <p className="fontSize--s fontColor--secondary">{label}</p>
      </Row.Item>
      <Row.Item>
        <p
          className="fontSize--s fontWeight--semibold"
          style={{
            color: "var(--color-mediumGrey)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={value}
        >
          {value}
        </p>
      </Row.Item>
    </Row>
  );
}
